import React, { useState } from "react";
import { countBy, isEmpty, keyBy, size } from "lodash";

import { API, graphqlOperation } from "aws-amplify";
import { createProduct, updateProduct } from "../../../graphql/mutations";

import {
  Backdrop,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Link,
  makeStyles,
  Typography,
} from "@material-ui/core";

import {
  ArrayField,
  Button,
  Datagrid,
  FileInput,
  TextField,
  useNotify,
  useRedirect,
} from "react-admin";
import { useForm } from "react-final-form";
import { v4 as uuidv4 } from "uuid";

import {
  AttachFileIcon,
  CancelIcon,
  CloseIcon,
  ImportIcon,
} from "../../../components/common/iconography";
import { FileField } from "../../../components/common/FileField";
import {
  batchMutations,
  isConditionalCheckFailed,
  logError,
  logHistory,
} from "../../../utils/appUtils";
import {
  CatalogHeaderMap,
  InventoryStatus,
  MAX_FILE_SIZE,
  MAX_FILE_SIZE_MSG,
  ResourceType,
  Unit,
  UnitConversionColumns,
  UnitOptions,
  UnitToColumn,
} from "../../../utils/constants";
import { csv2arr } from "../../../utils/fileUtils";
import {
  customHeadersToInventoryFields,
  getCustomHeaders,
} from "./AccountUtils";
import { sanitizeProductEdit } from "../../../graphql/account/accountCRUD";
import { computeLowInventoryAlert } from "../products/ProductUtils";

const useStyles = makeStyles((theme) => ({
  scrollableGrid: {
    overflowX: "auto",
    overflowY: "hidden",
    width: "100%",
    border: "1px solid #ccc",
    borderRadius: 4,
    paddingBottom: 8,
    "&::-webkit-scrollbar": {
      height: "8px",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#888",
      borderRadius: "4px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      backgroundColor: "#555",
    },
  },
  dialogContent: {
    // https://stackoverflow.com/questions/47698037/how-can-i-set-a-height-to-a-dialog-in-material-ui
    height: "90vh",
  },
  dialogTitle: {
    display: "flex",
    paddingTop: "5px",
    paddingBottom: "5px",
    justifyContent: "space-between",
  },
}));

const validateCSV = (csvArray, errors) => {
  const units = Object.keys(Unit);
  let missingCode = false;
  let missingUnits = false;
  const invalidUnits = new Set();
  let missingCmmodity = false;
  const invalidCmmodity = new Set();
  let missingUnitValue = false;
  let invalidConverts = false;
  let invalidLowAlert = false;
  let negativeLowAlert = false;
  const commodities = window.appUser.reloadYard.yardCommodities.items;

  const dupCodes = Object.entries(countBy(csvArray, "productCode")).reduce(
    (acc, [key, val]) => {
      return key !== "" && val > 1 ? acc.concat(key) : acc;
    },
    [],
  );
  if (dupCodes?.length > 0)
    errors.push(
      `Duplicate "Product Code" entries: ${dupCodes.map((dup) => ` ${dup}`)}`,
    );

  debugger;
  csvArray.forEach((row) => {
    // check for duplicates & missing codes
    row.error = row.error || dupCodes.some((dup) => dup === row["productCode"]);
    if (row["productCode"] === "") {
      missingCode = true;
      row.error = true;
    }

    // check for correct units
    let invalid = !units.includes(row["defaultUOM"]);
    if (invalid) {
      row.error = true;
      if (row["defaultUOM"].trim() === "") missingUnits = true;
      else invalidUnits.add(row["defaultUOM"]);
    }

    // check for correct commmodities
    const ryCommodity = commodities.find(
      (comm) => comm.commodity.name === row["reloadYardCommodityId"],
    );
    if (!ryCommodity) {
      row.error = true;
      if (row["reloadYardCommodityId"].trim() === "") missingCmmodity = true;
      else invalidCmmodity.add(row["reloadYardCommodityId"]);
    } else {
      row["reloadYardCommodityId"] = ryCommodity.id;
      row["commodityCateory"] = ryCommodity.commodity.name;
    }

    // check all units for numeric => 0
    const rowUnits = UnitConversionColumns.map((col) => row[col]);
    invalidConverts = rowUnits.some((val) => {
      const unitVal = Number(val); // null = 0, undefined = NaN
      return isNaN(unitVal) || unitVal < 0; // null = false, undefined = true
    });
    if (invalidConverts) row.error = true;

    // check if default & billing units are calculated from default units if != default units
    if (!invalidUnits) {
      let uom = row["defaultUOM"];
      if (uom !== Unit.CAR && uom !== Unit.TL && !row[UnitToColumn[uom]]) {
        missingUnitValue = true;
        row.error = true;
      }
    }

    // low inventory alert number >= 0
    const low = Number(row["lowInventoryThreshold"]); // Number(null) = 0
    if (isNaN(low)) {
      row.error = true;
      invalidLowAlert = true;
    } else if (low < 0) negativeLowAlert = true;
  });

  if (missingCode) errors.push(`Missing "Product Code" entries.`);

  if (missingUnits) errors.push(`Missing "Default/Billing Units" entries.`);
  if (invalidUnits.size > 0)
    errors.push(
      `Invalid "Default/Billing Units" entries: ${[...invalidUnits].map(
        (dup) => ` ${dup}`,
      )}`,
    );

  if (missingCmmodity) errors.push(`Missing "Load Type" entries.`);
  if (invalidCmmodity.size > 0)
    errors.push(
      `Invalid "Load Type" entries: ${[...invalidCmmodity].map(
        (dup) => ` ${dup}`,
      )}`,
    );

  if (missingUnitValue)
    errors.push(`Default/Billing Unit columns must be greater than 0.`);

  if (invalidConverts)
    errors.push(
      `Conversion unit values must be numeric, greater than 0, or blank.`,
    );

  if (invalidLowAlert)
    errors.push(`Invalid "Low Inventory Threshold" entries.`);
  if (negativeLowAlert)
    errors.push(`"Low Inventory Threshold" must be 0 or greater.`);

  debugger;
  return errors;
};

const processCSV = async (
  str,
  delim = ",",
  setCustomHeaders,
  setCSVData,
  setCSVErrors,
  notify,
) => {
  let errors = [];
  let lengthError = false;
  debugger;

  try {
    let headers = str
      .slice(0, str.indexOf("\n"))
      .replace("\r", "")
      .split(delim);
    headers = headers.filter((head) => head !== "");
    const hlen = headers.length;

    let columnCount = 0;
    let headerColumns = [];
    let customHeaders = [];
    headers.forEach((h) => {
      const col = CatalogHeaderMap[h.trim()]; // Trim header names
      debugger;
      if (col) {
        headerColumns.push(col);
        columnCount++;
      } else {
        const newHeader = h;
        customHeaders.push(newHeader);
        headerColumns.push(newHeader);
      }
    });
    const invalidHeaders = columnCount !== size(CatalogHeaderMap);
    if (invalidHeaders) {
      notify("Modified or unrecognized column headers.", "warning");
      setCSVErrors(["Modified or unrecognized column headers."]);
      setCSVData({});
      return;
    }

    let rows = csv2arr(str.slice(str.indexOf("\n") + 1));
    rows = rows.filter((row) => row.join("") !== "");

    let newArray = rows
      .map((row) => {
        if (!isEmpty(row) && row.length > 1) {
          const eachObject = headerColumns.reduce((obj, header, i) => {
            obj[header] = row[i].trim(); // Trim each cell value
            return obj;
          }, {});

          if (row.length > hlen) {
            eachObject["error"] = true;
            lengthError = true;
          }

          return eachObject;
        }
        return null; // Explicitly return null for rows that don't meet the condition
      })
      .filter((itm) => itm);

    if (lengthError)
      errors.push(
        `Values in extra columns, or extra unquoted commas within entries.`,
      );
    errors = validateCSV(newArray, errors);
    if (errors.length > 0) {
      setCSVErrors(errors);
      newArray = newArray.filter((row) => row.error);
      setCSVData({ preview: newArray.slice(0, 20) });
    } else {
      setCSVData({ products: newArray, preview: newArray.slice(0, 20) });
      setCustomHeaders(customHeaders);
      notify("Product Catalog uploaded successfully.", "info");
    }
  } catch (err) {
    logError("accounts:CatalogUpload:processCSV", err, notify);
  }
};

const ErrorTextField = (props) => (
  <TextField color={props.record.error ? "error" : "body"} {...props} />
);

export const CatalogUpload = ({
  csvData,
  setCSVData,
  csvErrors,
  setCSVErrors,
  ...props
}) => {
  const classes = useStyles();
  const form = useForm();
  const notify = useNotify();
  const [customHeaders, setCustomHeaders] = useState([]);

  const handleCSVFile = async (value) => {
    try {
      setCSVErrors(null);
      if (!value) {
        form.change("productFile", null);
        setCSVData({});
      } else if (value?.size > MAX_FILE_SIZE) {
        notify(MAX_FILE_SIZE_MSG, "warning");
        form.change("productFile", null);
        setCSVData({});
      } else {
        const fileState = form.getFieldState("productFile");
        const reader = new FileReader();
        reader.onload = function (e) {
          const text = e.target.result;
          processCSV(
            text,
            ",",
            setCustomHeaders,
            setCSVData,
            setCSVErrors,
            notify,
          );
        };
        let blob = await fetch(fileState.value.src).then((r) => r.blob());
        reader.readAsText(blob);
      }
    } catch (error) {
      logError("accounts:CatalogUpload:handleCSVFile", error, notify);
    }
  };

  return (
    <Grid container direction="column" spacing={1} fullWidth>
      <Grid container direction="row" spacing={4}>
        <Grid item xs={12} md={6}>
          <Typography variant="overline" color="textSecondary" gutterBottom>
            Instructions
          </Typography>
          <ol>
            <li>
              {" "}
              Download{" "}
              <Link
                href="https://transloads.co/docs/product-catalog-bulk-upload"
                target="_blank"
                rel="noreferrer"
                underline="hover"
              >
                Product_Catalog_Template.xlsx
              </Link>{" "}
              spreadsheet
            </li>
            <li>
              {" "}
              Fill out following the instructinos and excamples on the download
              page{" "}
            </li>
            <li>
              {" "}
              Save as a comma-separated-values (.CSV) file and upload here{" "}
            </li>
            <li>
              {" "}
              If errors are found, review, correct the errors and re-upload{" "}
            </li>
            <li> For help, contact us at 877-TRANSLOAD / 877-872-6756 </li>
          </ol>
        </Grid>
        <Grid item xs={12} md={6}>
          <FileInput
            source="productFile"
            label="CATALOG IMPORT"
            variant="outlined"
            fullWidth
            placeholder={
              <p>
                <AttachFileIcon fontSize="large" /> <br /> Drop a CSV File to
                upload, or click to select it.
              </p>
            }
            accept=".csv"
            onChange={handleCSVFile}
            helperText={false}
          >
            <FileField source="src" title="title" target="_blank" />
          </FileInput>
        </Grid>
      </Grid>
      <Grid container direction="row" spacing={1}>
        {csvErrors && (
          <Grid item xs={12}>
            <Typography variant="overline" color="error">
              {" "}
              Loading Errors:{" "}
            </Typography>
            <ul>
              {csvErrors.map((error) => (
                <Typography color="error">
                  {" "}
                  <li>{error}</li>{" "}
                </Typography>
              ))}
            </ul>
          </Grid>
        )}
        {csvData?.preview?.length > 0 && (
          <>
            <Grid item xs={12}>
              <Typography variant="overline" color="textSecondary" gutterBottom>
                {" "}
                Upload Preview (top 20 rows):{" "}
              </Typography>
            </Grid>
            <Grid item xs={12} className={classes.scrollableGrid}>
              <ArrayField source="preview" record={csvData} fullWidth>
                <Datagrid>
                  <ErrorTextField source="productCode" label="Product Code" />
                  <ErrorTextField source="name" label="Product Name" />
                  <ErrorTextField source="commodityCateory" label="Load Type" />
                  <ErrorTextField
                    source="category"
                    label="Commodity Category"
                  />
                  <ErrorTextField
                    source="defaultUOM"
                    label="Units of Measure"
                  />
                  {customHeaders.map((field) => (
                    <ErrorTextField source={field} label={field} />
                  ))}
                </Datagrid>
              </ArrayField>
            </Grid>
          </>
        )}
      </Grid>
    </Grid>
  );
};

export const CatalogUploadDialog = ({
  account,
  existingProducts,
  uploadOpen,
  setUploadOpen,
  handleRefresh,
}) => {
  const classes = useStyles();
  const form = useForm();
  const notify = useNotify();
  const redirect = useRedirect();
  const [csvData, setCSVData] = useState({});
  const [csvErrors, setCSVErrors] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const handleCatalogUpload = async () => {
    if (csvData.products?.length > 0) {
      try {
        debugger;
        setIsSaving(true);
        // Use currentProducts state instead of form field
        const oldProductMap =
          existingProducts.length > 0
            ? keyBy(existingProducts, "productCode")
            : {};
        const unitEntries = UnitOptions.filter(
          (e) => e.id !== Unit.CAR && e.id !== Unit.TL,
        );
        const customHeaders = getCustomHeaders(csvData.products[0]);

        // A catalog file has no row cap, so the mutations go out in capped
        // batches with retry rather than one fan-out per row.
        //
        // A new product's id is minted here, once per row, rather than inside
        // the thunk batchMutations re-invokes on a retry: it has to be the
        // same id on every attempt, which is the whole point of fixing it (see
        // the create below).
        const operations = csvData.products.map((item) => {
          const newProductId = uuidv4();
          return () => {
            // check old vs new products
            const oldProduct = oldProductMap[item["productCode"]];
            if (oldProduct) {
              let input = { ...oldProduct, ...item };
              input.status = InventoryStatus.STORED; // used for inventories connection

              input.lowInventoryThreshold = Number(
                item["lowInventoryThreshold"],
              ); // Number parse to 0
              input.pcsHeight = parseFloat(item["pcsHeight"]);
              input.pcsWidth = parseFloat(item["pcsWidth"]);
              input.pcsLength = parseFloat(item["pcsLength"]);
              input.height = parseFloat(item["height"]);
              input.width = parseFloat(item["width"]);
              input.length = parseFloat(item["length"]);

              unitEntries.forEach((unit) => {
                input[unit.column] = parseFloat(item[unit.column]);
              });

              if (customHeaders.length > 0) {
                input.customFields = [];
                customHeaders.forEach((header) => {
                  input.customFields.push(`${header}||${item[header]}`);
                  delete input[header];
                });
              }

              // Judged against the totals the list was loaded with; the lambda
              // refreshes the flag on the next inventory event either way.
              input = sanitizeProductEdit(input);
              input.lowInventoryAlert = computeLowInventoryAlert(
                oldProduct,
                input.lowInventoryThreshold,
              );

              // cleanup side-effects
              delete input.commodityCateory;
              delete input.error;

              return API.graphql(graphqlOperation(updateProduct, { input }));
            } else {
              const input = {
                active: "true",
                groups: account.groups,
                reloadYardId: account.reloadYardId,
                accountId: account.id,
                status: InventoryStatus.STORED, // used for inventories connection
              };

              input.productCode = item["productCode"];
              input.name = item["name"];
              input.reloadYardCommodityId = item.reloadYardCommodityId;
              input.category = item["category"] ? item["category"] : null;
              input.defaultUOM = item["defaultUOM"];
              // temporarily disabled account overrides untill needed
              // input.defaultWeightUOM = item["defaultWeightUOM"]
              //   ? item["defaultWeightUOM"]
              //   : null;
              input.lowInventoryThreshold = Number(
                item["lowInventoryThreshold"],
              ); // Number parse to 0
              input.height = parseFloat(item["height"]);
              input.width = parseFloat(item["width"]);
              input.length = parseFloat(item["length"]);

              unitEntries.forEach(({ column }) => {
                input[column] = parseFloat(item[column]);
              });

              input.totalQuantityOnHand = 0;
              input.totalQuantityCommitted = 0;
              input.lowInventoryAlert = false;

              if (customHeaders.length > 0) {
                input.customFields = [];
                customHeaders.forEach((header) => {
                  input.customFields.push(`${header}||${item[header]}`);
                });
              }

              // The id is the row's, not the resolver's, so a retry writes the
              // row it already wrote. batchMutations retries a network error or
              // a timeout, and a create whose response was lost commits
              // server-side all the same: with a resolver-minted id each retry
              // made another row under the same productCode, which then breaks
              // the keyBy(existingProducts, "productCode") the next upload
              // reads. The resolver's attribute_not_exists(id) turns the second
              // write into a conditional failure instead, and that failure IS
              // the row being there already — counted as written, not as lost.
              input.id = newProductId;
              return API.graphql(
                graphqlOperation(createProduct, { input }),
              ).catch((err) => {
                if (isConditionalCheckFailed(err))
                  return { data: { createProduct: { id: newProductId } } };
                throw err;
              });
            }
          };
        });

        const results = await batchMutations(operations, {
          batchSize: 25,
          maxRetries: 3,
          retryDelay: 1000,
          batchDelay: 100,
        });

        // Log history for each successfully created and updated product
        const successfulCreates = results
          .filter(
            (result) => result.success && result.result?.data?.createProduct,
          )
          .map((result) => result.result.data.createProduct);

        const successfulUpdates = results
          .filter(
            (result) => result.success && result.result?.data?.updateProduct,
          )
          .map((result) => result.result.data.updateProduct);

        // Log history for created products
        for (const product of successfulCreates) {
          const historyNote = {
            parentId: product.id,
            parentType: ResourceType.PRODUCT,
            text: "Product Imported.",
          };
          logHistory(historyNote);
        }

        // Log history for updated products
        for (const product of successfulUpdates) {
          const historyNote = {
            parentId: product.id,
            parentType: ResourceType.PRODUCT,
            text: "Bulk product update.",
          };
          logHistory(historyNote);
        }

        customHeadersToInventoryFields(form, customHeaders, account);

        // How many rows landed, not just the first thing that went wrong: a
        // file too big for one run can fail a handful of rows halfway through,
        // and an import that says nothing about them reads as a clean run.
        // Every failure is logged so the console carries what the notify
        // cannot; the import is re-runnable, since a row already written is
        // matched by productCode and updated rather than created again.
        const failures = results.filter((itm) => !itm.success);
        const written = successfulCreates.length + successfulUpdates.length;
        if (failures.length) {
          failures.forEach((itm) =>
            console.error("Catalog import row failed:", itm.index, itm.error),
          );
          notify(
            `${written} of ${results.length} products imported — ${failures.length} failed. ` +
              "Run the import again to retry the rest.",
            "warning",
          );
        } else {
          notify(`${written} products imported successfully`, "success");
        }
      } catch (error) {
        logError(
          "accounts:AccountEdit:handleCatalogUpload",
          error,
          notify,
          redirect,
        );
      } finally {
        handleRefresh && handleRefresh();
        setIsSaving(false);
      }
    }
    handleClose();
  };

  const handleClose = () => {
    setCSVData({});
    setCSVErrors(null);
    setUploadOpen(false);
  };

  return (
    <Dialog maxWidth="xl" open={uploadOpen} fullWidth>
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <h2> Catalog Import </h2>
        <IconButton aria-label="close" onClick={handleClose}>
          <CloseIcon fontSize="large" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers className={classes.dialogContent}>
        <Backdrop className={classes.backdrop} open={isSaving}>
          <CircularProgress color="inherit" />
        </Backdrop>
        <CatalogUpload
          csvData={csvData}
          setCSVData={setCSVData}
          csvErrors={csvErrors}
          setCSVErrors={setCSVErrors}
        />
      </DialogContent>
      <DialogActions>
        <Button label="Cancel" color="default" autoFocus onClick={handleClose}>
          <CancelIcon />
        </Button>
        <Button
          label="Import"
          color="primary"
          onClick={handleCatalogUpload}
          disabled={!csvData?.products?.length || csvErrors || isSaving}
        >
          <ImportIcon />
        </Button>
      </DialogActions>
    </Dialog>
  );
};
